import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
// import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
} from "@/components/ui/table";
// import {
//   Dialog,
//   DialogClose,
//   DialogContent,
//   DialogDescription,
//   DialogFooter,
//   DialogHeader,
//   DialogTitle,
//   DialogTrigger,
// } from "@/components/ui/dialog";

import { BilanOptions } from "@/data/BilanOptions";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Save } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useParams } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { makeAdaptedData } from "@/utils/makeAdaptedData";

const formSchema = z.object({
  parameters: z.object({
    Bilan_Cv: z.object({
      selected: z.boolean(),
      sous_module: z.string(),
    }),
    Bilan_Lecture: z.object({
      selected: z.boolean(),
      contenu: z.string().optional(),
      sous_module: z.string(),
    }),
    Bilan_Exploration: z.object({
      selected: z.boolean(),
      sous_module: z.string(),
    }),
  }),
  notes: z.string().optional(),
  sujet: z.string().max(40).optional(),
});

export default function BilanComponent() {
  const { patientId, sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      parameters: {
        Bilan_Cv: {
          selected: false,
          sous_module: "Bilan_Cv",
        },
        Bilan_Lecture: {
          selected: false,
          contenu: "1",
          sous_module: "Bilan_Lecture",
        },
        Bilan_Exploration: {
          selected: false,
          sous_module: "Bilan_Exploration",
        },
      },
      notes: "",
      sujet: "",
    },
  });

  useEffect(() => {
    if (sessionId) {
      axios
        .get(`/practitioner/session/${sessionId}`)
        .then((respSess) => {
          const sessionData = respSess.data;
          const adaptedData = makeAdaptedData(sessionData); // votre parsing
          form.reset(adaptedData);
        })
    }
  }, [patientId, sessionId, form]);

  const handleSubmite = async (data: z.infer<typeof formSchema>) => {
    setLoading(true);
    try {
      const selectedModules = Object.keys(data.parameters).filter(
        (key) => data.parameters[key as keyof typeof data.parameters].selected
      );
      const formData = {
        ...data,
        status: "PREVUE",
        type: selectedModules,
        sessionType: "BILAN",
      };

      if (sessionId) {
        await axios.put("/practitioner/session/" + sessionId, formData);
        toast.success("La séance a bien été modifiée");
      } else {
        await axios.post("/practitioner/session/" + patientId, formData);
        toast.success("La séance a bien été créée");
      }

      // Navigation seulement après confirmation du backend pour éviter la course
      navigate(`/praticien/${patientId}/dossier`);
    } catch (error) {
      toast.error("Une erreur est survenue lors de la sauvegarde de la séance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="pb-4 pr-6 pl-6 w-5/6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmite)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-left">Selection</TableHead>
                <TableHead>Exercice</TableHead>
                <TableHead className="text-left">Paramètres</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-module-champ-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.Bilan_Cv.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                <TableCell className="text-left">
                  {BilanOptions.Bilan.sous_module}
                </TableCell>
                <TableCell className="text-left"></TableCell>
              </TableRow>
              <TableRow className="bg-module-optocinetique-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.Bilan_Lecture.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                <TableCell className="text-left">
                  {BilanOptions.Lecture.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  <FormField
                    control={form.control}
                    name="parameters.Bilan_Lecture.contenu"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Choix du texte</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="w-[250px]">
                              <SelectValue placeholder="Selectionner la couleur de la cible" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {BilanOptions.Lecture.parametre.contenu.map(
                              (option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow className="bg-module-poursuite-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.Bilan_Exploration.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                <TableCell className="text-left">
                  {BilanOptions.Exploration.sous_module}
                </TableCell>
                <TableCell className="text-left"></TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="w-3/4 ml-4 mt-6">
                <div className="text-left ml-11">
                  <FormLabel>Notes</FormLabel>
                </div>
                <FormControl>
                  <Textarea
                    placeholder="Ajouter un commentaire à la séance"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="mt-6" disabled={loading}>
            <Save className="mr-2" />
            {loading ? "Enregistrement..." : "Valider le Bilan"}
          </Button>
        </form>
      </Form>
    </Card>
  );
}
